import moment from 'moment-timezone';

export function serializeDate(value) {
	if (!value) return null;
	if (moment.isMoment(value)) return value.toISOString();
	if (value instanceof Date) return value.toISOString();
	return value;
}

export function serializeDateRange(value) {
	if (!value?.length) return null;
	return value.map(serializeDate);
}

export function deserializeDate(value) {
	if (!value) return null;
	return moment(value).toDate();
}

export function deserializeDateRange(value) {
	if (!value?.length) return null;
	return value.map(deserializeDate);
}

export function serializeEntityRef(entity) {
	if (!entity) return null;
	if (Array.isArray(entity)) {
		return entity
			.map(item => ({ id: item.id, value: item.value }))
			.filter(item => item.id != null || item.value != null);
	}
	if (entity.id != null) return { id: entity.id };
	if (entity.value != null) return { value: entity.value };
	return null;
}

export function serializeOptionValues(options) {
	if (!options?.length) return null;
	return options.map(option => option.value);
}

export function resolveOptionsByValue(values, options) {
	if (!values?.length) return null;
	const optionByValue = new Map(options.map(option => [option.value, option]));
	return values.map(value => optionByValue.get(value)).filter(Boolean);
}

export function resolveEntityRefs(refs) {
	if (!refs?.length) return null;
	return refs
		.map(ref => {
			if (ref.id != null) return { id: ref.id };
			if (ref.value != null) return { value: ref.value };
			return null;
		})
		.filter(Boolean);
}

export function stableStringify(value) {
	return JSON.stringify(value, (_key, val) => {
		if (val instanceof Date) return val.toISOString();
		return val;
	});
}

export function snapshotsEqual(a, b) {
	return stableStringify(a) === stableStringify(b);
}

/** Wait for the next paint so Floating UI / ResizeObserver can settle before layout shifts. */
export function deferToNextFrame() {
	return new Promise(resolve => {
		requestAnimationFrame(() => requestAnimationFrame(resolve));
	});
}

export function pickQueryForView(query) {
	if (!query) return {};
	const { pagination, ...rest } = query;
	return rest;
}

/** Read search text from page state or list query (text vs title varies by page). */
export function captureSearchFilter(pageStateSearch, query = {}) {
	return pageStateSearch || query.text || query.title || null;
}

/** Always return a string so the search InputField stays controlled and clears on view switch. */
export function resolveSearchFilterForApply(uiSearch, query = {}) {
	const value = uiSearch ?? query.text ?? query.title ?? '';
	return value || '';
}

export function pickViewQueryForView(
	query,
	keys,
	defaultSort = { column: 'createdAt', asc: false }
) {
	const rest = pickQueryForView(query);
	const picked = {
		sort: rest.sort || defaultSort
	};

	keys.forEach(key => {
		const value = rest[key];
		if (value == null) return;
		if (Array.isArray(value) && value.length === 0) return;
		if (typeof value === 'string' && value.trim() === '') return;
		picked[key] = value;
	});

	return picked;
}

export function normalizeEntityRefIds(refs) {
	if (!refs?.length) return null;

	const ids = refs
		.map(ref => {
			if (typeof ref === 'number' || typeof ref === 'string') {
				return Number(ref);
			}
			return ref?.id;
		})
		.filter(id => id != null)
		.sort((a, b) => a - b);

	return ids.length ? ids : null;
}

export function normalizeCreatableMultiValue(items) {
	if (!items?.length) return null;

	const values = items
		.map(item => {
			if (item == null) return null;
			if (typeof item === 'string' || typeof item === 'number')
				return String(item);
			return item.value != null ? String(item.value) : null;
		})
		.filter(Boolean)
		.sort();

	return values.length ? values : null;
}

export function normalizeStatusQueryValue(status) {
	if (status == null) return null;
	if (Array.isArray(status)) {
		const values = status.filter(value => value != null).sort();
		if (values.length === 0) return null;
		return values.length === 1 ? values[0] : values;
	}
	return status;
}

export function normalizeUiState(ui = {}) {
	const normalized = {};
	Object.entries(ui).forEach(([key, value]) => {
		if (value == null) return;
		if (typeof value === 'boolean' && value === false) return;
		if (Array.isArray(value) && value.length === 0) return;
		if (typeof value === 'string' && value.trim() === '') return;
		if (
			typeof value === 'object' &&
			!Array.isArray(value) &&
			Object.keys(value).length === 0
		) {
			return;
		}
		normalized[key] = value;
	});
	return normalized;
}

export function normalizeQueryState(
	query = {},
	defaultSort = { column: 'createdAt', asc: false }
) {
	const normalized = {
		sort: query.sort || defaultSort
	};

	Object.entries(query).forEach(([key, value]) => {
		if (key === 'sort') return;
		if (value == null) return;
		if (Array.isArray(value) && value.length === 0) return;
		if (typeof value === 'string' && value.trim() === '') return;
		if (key === 'status') {
			const status = normalizeStatusQueryValue(value);
			if (status != null) normalized.status = status;
			return;
		}
		if (key === 'title' || key === 'text') {
			normalized.text = value;
			return;
		}
		normalized[key] = value;
	});

	return normalized;
}

export function normalizeGlobalQueryState(globalQuery = {}) {
	const normalized = {};
	Object.entries(globalQuery).forEach(([key, value]) => {
		if (value == null) return;
		if (Array.isArray(value) && value.length === 0) return;
		if (typeof value === 'string' && value.trim() === '') return;
		normalized[key] = value;
	});
	return normalized;
}
